import { AnnotationSetType, AnnotationType, BarMitzvahType } from 'types/types'
import { computed, Ref, watch } from 'vue'

import { useAnnotationCache } from '@/composables/useAnnotationCache'
import { mergeAnnotationStyles, parseAnnotationStyle, serializeAnnotationStyle } from '@/util/annotationStyle'

/**
 * Merge two annotation arrays at the same char indices.
 * When both global and overlay annotations exist at the same index,
 * the overlay's style keys take precedence (spread over global).
 */
function mergeAnnotationArrays(global: AnnotationType[], overlay: AnnotationType[]): AnnotationType[] {
  const byIndex = new Map<number, AnnotationType>()
  for (const a of global) {
    byIndex.set(a.index, { ...a })
  }
  for (const a of overlay) {
    const existing = byIndex.get(a.index)
    if (existing) {
      existing.style = serializeAnnotationStyle(mergeAnnotationStyles(parseAnnotationStyle(existing.style), parseAnnotationStyle(a.style)))
    } else {
      byIndex.set(a.index, { ...a })
    }
  }
  return [...byIndex.values()]
}

/**
 * Thin wrapper around `useAnnotationCache` that watches a barMitzvah ref,
 * fetches annotations when it changes, and returns a reactive annotations array.
 *
 * When overlayId is provided, returns merged global (overlay=null) + matching
 * overlay annotation sets. Without overlayId, returns only global sets.
 */
export function useAnnotations(barMitzvah: Ref<BarMitzvahType | null | undefined>, overlayId?: Ref<string | null | undefined>) {
  const { cache, fetchAnnotations } = useAnnotationCache()

  const annotations = computed<AnnotationSetType[]>(() => {
    if (!barMitzvah.value) {
      return []
    }
    const allSets = cache.get(barMitzvah.value.student.username) ?? []
    const oid = overlayId?.value

    if (!oid) {
      // No overlay: return only global (teacher) annotation sets
      return allSets.filter((s) => !s.overlay)
    }

    // With overlay: merge global sets + matching overlay sets per sentence
    const globalBySentence = new Map<string, AnnotationSetType>()
    const overlayBySentence = new Map<string, AnnotationSetType>()

    for (const set of allSets) {
      const sid = set.sentence.id
      if (!set.overlay) {
        globalBySentence.set(sid, set)
      } else if (set.overlay.id === oid) {
        overlayBySentence.set(sid, set)
      }
    }

    const result: AnnotationSetType[] = []
    const seenSentences = new Set<string>()

    // Process sentences that have overlay annotations
    for (const [sid, overlaySet] of overlayBySentence) {
      seenSentences.add(sid)
      const globalSet = globalBySentence.get(sid)
      if (globalSet) {
        // Merge global + overlay annotations
        result.push({
          ...overlaySet,
          annotations: mergeAnnotationArrays(globalSet.annotations ?? [], overlaySet.annotations ?? []),
        })
      } else {
        result.push(overlaySet)
      }
    }

    // Include global-only sentences (no overlay annotations for this sentence)
    for (const [sid, globalSet] of globalBySentence) {
      if (!seenSentences.has(sid)) {
        result.push(globalSet)
      }
    }

    return result
  })

  watch(
    () => !!barMitzvah.value,
    (hasBm) => {
      if (hasBm) {
        fetchAnnotations(barMitzvah.value!.student.username)
      }
    },
    { immediate: true },
  )

  return { annotations }
}
